import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Loader2,
  MessageCircleQuestion,
  RotateCw,
  Send,
  X,
} from 'lucide-react';
import type { ManualRole } from '@shared/manual/types';
import type { UserRole } from '@shared/types';
import { useAuth } from '@/lib/auth';
import { useEffectiveRole } from '@/features/impersonation/useImpersonation';
import { renderMarkdownLite } from '@/features/roldi/markdownLite';
import { cn } from '@/lib/utils';
import { VoiceDictateButton, appendDictation } from '@/features/voice/VoiceDictateButton';
import { useHelpStream, type HelpHistoryTurn, type HelpSource } from './useHelpStream';
// The open-event name and the CTA affordances live in one module so the manual
// and the bubble cannot drift onto two different event strings.
import { OPEN_HELP_EVENT } from './AskFleetwiseCta';

/* ============================================================================
 * The floating bubble itself.
 *
 * Mounted once near the app root (App.tsx). It answers how the PRODUCT works,
 * grounded in the same manual `/help` renders, and it knows which page you're
 * standing on and which role you're in. It is explicitly NOT a diagnostic
 * assistant — truck questions belong to Roldi in Diagnose and Lisa on the
 * manager side, and the agent says so rather than guessing.
 *
 * Ephemeral by design: the thread lives in React state for this session only.
 * Nothing is persisted, here or on the server.
 * ========================================================================= */

/** Server caps history at 20 entries; ~10 keeps latency and spend sane. */
const MAX_HISTORY_TURNS = 10;
/** Manual links under an answer. More than three is a wall, not a pointer. */
const MAX_MANUAL_LINKS = 3;

interface HelpMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: HelpSource[];
  /** An error bubble — never replayed as context, never treated as an answer. */
  failed?: boolean;
}

interface RoleCopy {
  greeting: string;
  scopeNote: string;
  starters: string[];
}

/**
 * The differences here are real, not decorative: a technician wants the next
 * tap, a leader wants to know what a number leaves out. The starters are the
 * three questions each role actually asks first.
 */
const ROLE_COPY: Record<ManualRole, RoleCopy> = {
  technician: {
    greeting: 'I can walk you through anything in Fleetwise — in plain words, no manual required.',
    scopeNote: 'I only cover how to use the app. Truck questions go to Roldi in Diagnose.',
    starters: [
      'How do I finish a job?',
      'How do I start a work order?',
      'How do I add a photo to a job?',
    ],
  },
  manager: {
    greeting:
      'Ask me about the shop side — the queues, estimates, customers, or what a screen is for.',
    scopeNote: 'I only cover how to use the app. Repair questions go to Lisa on Ask Lisa.',
    starters: [
      'What is Ready to bill?',
      'How do I approve an estimate?',
      'How do I assign a line to a tech?',
    ],
  },
  leadership: {
    greeting:
      'Ask me what a number on your dashboards actually means — what it counts, and what it leaves out.',
    scopeNote: 'I only cover how to use the app, not the shop data behind it.',
    starters: [
      'What does cost per truck mean?',
      'What do the trends screens show?',
      'Where do I find the audit log?',
    ],
  },
  admin: {
    greeting:
      'Ask me about running Fleetwise itself — people, logins, the agents, and the knowledge base.',
    scopeNote: 'I only cover how to use the app, not the shop data inside it.',
    starters: [
      'How do I add a technician?',
      'How do I set a PIN for a technician?',
      'How do I teach Roldi something new?',
    ],
  },
};

/** Mirrors the server's own role narrowing so the two can't disagree. */
function toManualRole(role: UserRole | null): ManualRole {
  return role === 'manager' || role === 'leadership' || role === 'admin' ? role : 'technician';
}

/**
 * The agent is instructed to end a grounded answer with `More: /help/...` on its
 * own line. Pull that off the body so it renders as a real link instead of a
 * path someone has to retype. Anything that isn't a plain in-app path is left in
 * the text untouched — a rendered link is a navigation promise, and `//host`
 * would leave the app entirely.
 */
export function splitMoreLink(text: string): { body: string; moreLink: string | null } {
  const lines = text.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i] ?? '';
    if (line.trim() === '') continue;
    const link = /^\s*More:\s*(\/[^\s<>"']+)\s*$/i.exec(line)?.[1];
    if (!link || link.startsWith('//')) return { body: text, moreLink: null };
    return { body: lines.slice(0, i).join('\n').trimEnd(), moreLink: link };
  }
  return { body: text, moreLink: null };
}

/**
 * Replay history for the next turn.
 *
 * The Messages API requires strictly alternating roles starting on a user turn,
 * and the server appends the new question itself — so a dropped or failed turn
 * must not leave two user messages back to back, and the window must not open
 * on an assistant turn.
 */
export function buildHelpHistory(
  messages: HelpMessage[],
  maxTurns = MAX_HISTORY_TURNS,
): HelpHistoryTurn[] {
  const turns: HelpHistoryTurn[] = [];
  for (const message of messages) {
    if (message.failed) continue;
    const content = message.content.trim();
    if (!content) continue;
    if (turns[turns.length - 1]?.role === message.role) continue;
    turns.push({ role: message.role, content });
  }
  if (turns[turns.length - 1]?.role === 'user') turns.pop();

  const windowed = turns.slice(-maxTurns);
  if (windowed[0]?.role === 'assistant') windowed.shift();
  return windowed;
}

interface ManualLink {
  to: string;
  label: string;
  sublabel: string | null;
}

/** The `More:` line first (it's the agent's own pick), then the retrieval hits. */
function buildManualLinks(
  sources: HelpSource[],
  moreLink: string | null,
  max = MAX_MANUAL_LINKS,
): ManualLink[] {
  const out: ManualLink[] = [];
  const seen = new Set<string>();

  function push(to: string, label: string, sublabel: string | null) {
    if (seen.has(to) || out.length >= max) return;
    seen.add(to);
    out.push({ to, label, sublabel });
  }

  if (moreLink) {
    const match = sources.find((s) => s.link === moreLink);
    push(moreLink, match?.title ?? 'Open the manual', match?.chapter ?? null);
  }
  for (const source of sources) push(source.link, source.title, source.chapter);
  return out;
}

/**
 * The app-wide mount point. Renders nothing when nobody is signed in, so the
 * bubble never appears on /login or /select-tech.
 */
export function AskFleetwise() {
  const { user } = useAuth();
  if (!user) return null;
  return <HelpBubble />;
}

function HelpBubble() {
  const { t } = useTranslation();
  const location = useLocation();
  const manualRole = toManualRole(useEffectiveRole());
  const staticCopy = ROLE_COPY[manualRole];
  // The tech is the audience the EN/ES ruling exists for — their copy reads
  // from the locale files (elite-audit F6, 2026-08-26). Manager/admin/
  // leadership copy stays as authored until those roles carry a Spanish user.
  const copy: RoleCopy =
    manualRole === 'technician'
      ? {
          greeting: t('helpAgent.tech.greeting'),
          scopeNote: t('helpAgent.tech.scopeNote'),
          starters: [
            t('helpAgent.tech.starter1'),
            t('helpAgent.tech.starter2'),
            t('helpAgent.tech.starter3'),
          ],
        }
      : staticCopy;
  const stream = useHelpStream();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [lastQuestion, setLastQuestion] = useState('');

  const titleId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(0);
  const wasOpenRef = useRef(false);

  // Any surface can pop the bubble open without importing this component.
  useEffect(() => {
    function onOpenHelp() {
      setOpen(true);
    }
    window.addEventListener(OPEN_HELP_EVENT, onOpenHelp);
    return () => window.removeEventListener(OPEN_HELP_EVENT, onOpenHelp);
  }, []);

  // Escape closes; focus lands in the composer so a keyboard user can type
  // immediately instead of tabbing through the panel chrome first.
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  // Return focus to the trigger on close (WCAG 2.4.3) — but only after a real
  // close, so the first render never steals focus from the page.
  useEffect(() => {
    if (wasOpenRef.current && !open) buttonRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  // Body scroll lock on phones only, where the panel is a modal bottom sheet.
  // On a desktop it's a popover beside live content, and freezing the page
  // behind it would be hostile — half the point is reading along.
  useEffect(() => {
    if (!open) return;
    if (!window.matchMedia('(max-width: 639px)').matches) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Keep the newest content in view as tokens arrive.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, stream.text, stream.streaming, open]);

  function nextId(): string {
    idRef.current += 1;
    return `help-${idRef.current}`;
  }

  async function ask(question: string): Promise<void> {
    const trimmed = question.trim();
    if (!trimmed || stream.streaming) return;

    // Built from the messages BEFORE this question — the server appends the
    // new user turn itself.
    const history = buildHelpHistory(messages);
    setDraft('');
    setLastQuestion(trimmed);
    setMessages((prev) => [...prev, { id: nextId(), role: 'user', content: trimmed }]);

    const result = await stream.send({
      question: trimmed,
      currentPath: location.pathname,
      history,
    });

    const answer = result.text.trim();
    setMessages((prev) => {
      const next = [...prev];
      // A turn can produce partial text AND then fail. Keep whatever arrived —
      // half an answer plus an honest "that cut out" beats silently dropping it.
      if (answer) {
        next.push({ id: nextId(), role: 'assistant', content: answer, sources: result.sources });
      }
      if (result.error) {
        next.push({ id: nextId(), role: 'assistant', content: result.error, failed: true });
      }
      return next;
    });
  }

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void ask(draft);
  }

  const lastMessage = messages[messages.length - 1];
  const canRetry = !stream.streaming && lastMessage?.failed === true && lastQuestion.length > 0;
  const closePanel = () => setOpen(false);

  // Surfaces that dock their own composer to the bottom edge. The bubble lands
  // ON that composer's Send button — measured with `elementFromPoint` at Send's
  // centre, which returned this FAB, so the surface's core action opened the
  // help agent instead (elite-audit P1, 2026-08-26).
  //
  // This list has now been wrong twice in the same way, so the failures are
  // written down rather than re-discovered: first it hid only below `lg`, and
  // at 1440 the bubble still covered Shop Chat's Send; then `/trucks/:id` was
  // missed entirely, and at 375 / scrollY 240 the disc sat on the Truck
  // Intelligence composer's Send button. Any NEW surface with a docked
  // composer belongs here — `TruckIntelPanel`, `ChatView` (tech and manager)
  // and `ShopChatPage` are the components that have one today.
  const onBottomComposerRoute =
    location.pathname.startsWith('/tech/diagnose') ||
    location.pathname.startsWith('/tech/chat') ||
    location.pathname.startsWith('/manager/ai') ||
    location.pathname.startsWith('/trucks/');

  // Hiding an OPEN panel with `display:none` would leave it open in state and
  // pop it back the moment the user navigates away again. Close it on arrival
  // instead: opening help on /manager and then clicking through to Lisa used to
  // leave the panel's full-viewport backdrop sitting over her Send button.
  useEffect(() => {
    if (onBottomComposerRoute) setOpen(false);
  }, [onBottomComposerRoute]);

  return (
    <div
      className={cn(
        // z-30, not z-40. A help affordance must never paint over a sheet or
        // a drawer; z-40 put it above everything except a z-50 modal, which
        // is how it ended up sitting on top of a work-order row.
        'fixed right-4 z-30 md:right-6 print:hidden',
        // Hidden at EVERY width on composer routes, not just below lg. The
        // original fix assumed "at lg+ the corner is clear"; at 1440 on Shop
        // Chat the bubble still sits on top of Send — the composer owns the
        // bottom-right corner at every size. /help and the header entry
        // points are unaffected.
        onBottomComposerRoute && 'hidden',
        // Clears the tech bottom nav when there IS one. The offset used to be
        // unconditional, but this bubble mounts at App level and appears on
        // every surface — so on a manager page at tablet width it reserved 76px
        // for a bar that does not exist there and floated up into the content,
        // sitting on top of a work-order row and taking its taps.
        // TechLayout sets --fw-bottom-nav-h while it is mounted; it defaults to
        // 0 everywhere else, which puts the bubble in the actual corner.
        'bottom-[calc(var(--fw-bottom-nav-h,0rem)+1.5rem+env(safe-area-inset-bottom))]',
        'lg:bottom-[calc(1.5rem+env(safe-area-inset-bottom))]',
      )}
    >
      {/* Click-outside-to-close. Dims on a phone (where the panel is modal),
          invisible on a desktop (where it's a popover over live content). The
          real close affordances are the X and Escape, so this is not a tab
          stop. */}
      {open ? (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={closePanel}
          className="fixed inset-0 cursor-default bg-[var(--fw-scrim)] sm:bg-transparent"
        />
      ) : null}

      {/* Rendered BEFORE the panel on purpose: the mobile sheet must paint over
          the trigger rather than leave it floating on top of its own dialog. */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={open ? t('helpAgent.closeAria') : t('helpAgent.openAria')}
        title={t('helpAgent.title')}
        className={cn(
          // A ghost disc, not a filled one. This was a 56px gradient-and-glow
          // circle — the single most saturated object on every screen in the
          // product, spent on the least consequential control there is, while
          // the manager's actual queue counts rendered in plain white. Filled
          // Signal Cyan means "this is your next action"; help is never that.
          // It stays 44px so a gloved thumb can still hit it.
          'relative inline-flex h-11 w-11 items-center justify-center rounded-full',
          'border-fw-border bg-fw-surface text-fw-accent border',
          'shadow-[var(--fw-shadow-sm)]',
          'transition-colors duration-150 motion-reduce:transition-none',
          'hover:bg-fw-surface-hover hover:border-fw-border-strong',
          'active:scale-95 motion-reduce:active:scale-100',
          'focus-visible:ring-fw-ring focus:outline-none focus-visible:ring-2',
          'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fw-ring-offset)]',
        )}
      >
        {open ? (
          <X size={18} strokeWidth={2} aria-hidden />
        ) : (
          <MessageCircleQuestion size={20} strokeWidth={1.9} aria-hidden />
        )}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={cn(
            // Phone: a bottom sheet. Tablet and up: a popover anchored to the
            // bubble. One element, two shapes — no duplicated markup.
            'fixed inset-x-0 bottom-0 flex h-[80dvh] max-h-[34rem] flex-col overflow-hidden',
            'border-fw-border bg-fw-surface-elevated rounded-t-2xl border-t',
            'pb-[env(safe-area-inset-bottom)] shadow-[var(--fw-shadow-xl)]',
            'sm:absolute sm:inset-x-auto sm:right-0 sm:bottom-full sm:mb-3 sm:pb-0',
            'sm:h-[32rem] sm:max-h-[calc(100dvh-9rem)] sm:w-[min(24rem,calc(100vw-2rem))]',
            'sm:rounded-2xl sm:border',
          )}
        >
          <header className="border-fw-border/70 bg-fw-surface flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span
                className="bg-fw-accent-soft text-fw-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                aria-hidden
              >
                <MessageCircleQuestion size={16} strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <h2 id={titleId} className="text-fw-text truncate text-sm font-semibold">
                  {t('helpAgent.title')}
                </h2>
                <p className="text-fw-text-muted truncate text-[11px] leading-tight">
                  {t('helpAgent.subtitle')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closePanel}
              aria-label={t('helpAgent.closeAria')}
              className="text-fw-text-muted hover:bg-fw-surface-hover hover:text-fw-text focus-visible:ring-fw-ring inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 active:scale-95 motion-reduce:active:scale-100"
            >
              <X size={16} strokeWidth={2} aria-hidden />
            </button>
          </header>

          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4"
          >
            {messages.length === 0 ? (
              <HelpWelcome copy={copy} disabled={stream.streaming} onPick={(q) => void ask(q)} />
            ) : (
              // `additions` only: a finished answer is announced once, as a
              // whole. The token-by-token preview below sits OUTSIDE this
              // region so it never machine-guns a screen reader.
              <ul role="log" aria-live="polite" aria-relevant="additions" className="space-y-3">
                {messages.map((message) => (
                  <li key={message.id}>
                    <HelpMessageRow message={message} onNavigate={closePanel} />
                  </li>
                ))}
              </ul>
            )}

            {stream.streaming ? (
              <div className="mt-3">
                {stream.text ? (
                  <HelpAnswer text={stream.text} sources={[]} onNavigate={closePanel} />
                ) : (
                  <HelpThinking />
                )}
              </div>
            ) : null}

            {canRetry ? (
              <div className="mt-3 flex justify-start">
                <button
                  type="button"
                  onClick={() => void ask(lastQuestion)}
                  className="border-fw-border bg-fw-surface text-fw-text-secondary hover:border-fw-border-strong hover:bg-fw-surface-hover hover:text-fw-text focus-visible:ring-fw-ring inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 active:scale-95 motion-reduce:active:scale-100"
                >
                  <RotateCw size={13} strokeWidth={2} aria-hidden />
                  Try again
                </button>
              </div>
            ) : null}

            <span role="status" className="sr-only">
              {stream.streaming ? 'Finding an answer.' : ''}
            </span>
          </div>

          <form
            onSubmit={onSubmit}
            className="border-fw-border/70 bg-fw-surface flex shrink-0 items-center gap-2 border-t px-3 py-3"
          >
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('helpAgent.placeholder')}
              aria-label={t('helpAgent.inputAria')}
              autoComplete="off"
              maxLength={2000}
              className="fw-input min-w-0 flex-1 px-3 text-sm"
            />
            {/* The help bubble is where someone asks a short question with a
                wrench in the other hand. It had no mic. */}
            <VoiceDictateButton
              size="md"
              onText={(t) => setDraft((d) => appendDictation(d, t))}
              disabled={stream.streaming}
              label="Ask by voice"
            />
            <button
              type="submit"
              disabled={stream.streaming || draft.trim().length === 0}
              aria-label="Send question"
              className={cn(
                'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
                'bg-[image:var(--fw-gradient-accent)] text-[var(--fw-accent-fg)]',
                'shadow-[var(--fw-shadow-sm)] transition-[filter,transform] duration-150',
                'hover:brightness-110 active:scale-95',
                'motion-reduce:transition-none motion-reduce:active:scale-100',
                'focus-visible:ring-fw-ring focus:outline-none focus-visible:ring-2',
                'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fw-ring-offset)]',
                'disabled:cursor-not-allowed disabled:opacity-45',
                'disabled:hover:brightness-100 disabled:active:scale-100',
              )}
            >
              {stream.streaming ? (
                <Loader2 size={16} strokeWidth={2} className="animate-spin" aria-hidden />
              ) : (
                <Send size={16} strokeWidth={2} aria-hidden />
              )}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function HelpWelcome({
  copy,
  disabled,
  onPick,
}: {
  copy: RoleCopy;
  disabled: boolean;
  onPick: (question: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div>
        <p className="text-fw-text text-sm leading-relaxed">{copy.greeting}</p>
        <p className="text-fw-text-muted mt-1.5 text-xs leading-relaxed">{copy.scopeNote}</p>
      </div>

      <div>
        <p className="text-fw-text-muted text-[11px] font-semibold tracking-wider uppercase">
          {t('helpAgent.startHere')}
        </p>
        <div className="mt-2 space-y-2">
          {copy.starters.map((starter) => (
            <button
              key={starter}
              type="button"
              disabled={disabled}
              onClick={() => onPick(starter)}
              className="border-fw-border bg-fw-surface text-fw-text-secondary hover:border-fw-border-strong hover:bg-fw-surface-hover hover:text-fw-text focus-visible:ring-fw-ring group flex min-h-11 w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:active:scale-100"
            >
              <span className="min-w-0">{starter}</span>
              <ArrowRight
                size={14}
                strokeWidth={2}
                aria-hidden
                className="text-fw-text-muted group-hover:text-fw-accent shrink-0 transition-colors duration-150"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function HelpMessageRow({ message, onNavigate }: { message: HelpMessage; onNavigate: () => void }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-fw-accent max-w-[88%] rounded-2xl rounded-br-md px-3.5 py-2.5 text-sm leading-relaxed text-[var(--fw-accent-fg)] shadow-[var(--fw-shadow-sm)]">
          {message.content}
        </div>
      </div>
    );
  }

  if (message.failed) {
    return (
      <div className="flex justify-start">
        <div className="border-fw-warning/40 bg-fw-warning-soft text-fw-text flex max-w-[92%] gap-2.5 rounded-2xl rounded-bl-md border px-3.5 py-2.5 text-sm leading-relaxed">
          <AlertTriangle
            size={16}
            strokeWidth={2}
            aria-hidden
            className="text-fw-warning mt-0.5 shrink-0"
          />
          <span>{message.content}</span>
        </div>
      </div>
    );
  }

  return (
    <HelpAnswer text={message.content} sources={message.sources ?? []} onNavigate={onNavigate} />
  );
}

function HelpAnswer({
  text,
  sources,
  onNavigate,
}: {
  text: string;
  sources: HelpSource[];
  onNavigate: () => void;
}) {
  const { body, moreLink } = splitMoreLink(text);
  const links = buildManualLinks(sources, moreLink);

  return (
    <div className="flex justify-start">
      <div className="border-fw-border bg-fw-surface text-fw-text max-w-[92%] rounded-2xl rounded-bl-md border px-3.5 py-2.5 text-sm shadow-[var(--fw-shadow-sm)]">
        <div className="leading-relaxed whitespace-pre-wrap">{renderMarkdownLite(body)}</div>

        {links.length > 0 ? (
          <div className="border-fw-border/60 mt-3 border-t pt-2.5">
            <p className="text-fw-text-muted text-[10px] font-semibold tracking-wider uppercase">
              Read more in the manual
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {links.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    onClick={onNavigate}
                    className="text-fw-accent hover:text-fw-accent-hover focus-visible:ring-fw-ring inline-flex min-h-11 items-start gap-1.5 rounded text-[13px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2"
                  >
                    <BookOpen size={13} strokeWidth={2} aria-hidden className="mt-[3px] shrink-0" />
                    <span className="underline-offset-2 hover:underline">
                      {link.label}
                      {link.sublabel ? (
                        <span className="text-fw-text-muted font-normal"> · {link.sublabel}</span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HelpThinking() {
  return (
    <div className="flex justify-start">
      <div className="text-fw-accent inline-flex items-center gap-1.5 rounded-full bg-[color-mix(in_oklab,var(--fw-accent)_14%,transparent)] px-3 py-1.5 text-xs font-semibold">
        <span className="inline-flex gap-0.5" aria-hidden>
          <span className="bg-fw-accent inline-block h-1 w-1 animate-bounce rounded-full motion-reduce:animate-none" />
          <span
            className="bg-fw-accent inline-block h-1 w-1 animate-bounce rounded-full motion-reduce:animate-none"
            style={{ animationDelay: '150ms' }}
          />
          <span
            className="bg-fw-accent inline-block h-1 w-1 animate-bounce rounded-full motion-reduce:animate-none"
            style={{ animationDelay: '300ms' }}
          />
        </span>
        Looking it up…
      </div>
    </div>
  );
}
